import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLicense, activateLicense } from '../services/api';
import './LicenseDetail.css';

// Formatierungsfunktion für Datum und Uhrzeit (konsistent mit LicenseManagement)
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return dateStr;
  }
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
};

function isLicenseExpired(license) {
  if (!license?.expires_at || license.license_type === 'PERMANENT') return false;
  const d = new Date(license.expires_at);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

function LicenseDetail() {
  const { t } = useTranslation();
  const { key } = useParams();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const loadLicense = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLicense(key);
      setLicense(data.license);
    } catch (err) {
      setError(err.message || t('admin.license_detail.errors.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [key, t]);

  useEffect(() => {
    loadLicense();
  }, [loadLicense]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!userEmail) {
      setError(t('admin.license_detail.errors.user_email_required'));
      return;
    }
    try {
      await activateLicense(key, userEmail);
      loadLicense();
      setUserEmail('');
    } catch (err) {
      setError(err.message || t('admin.license_detail.errors.activate_failed'));
    }
  };

  if (loading) {
    return <div className="loading">{t('admin.license_detail.loading')}</div>;
  }

  if (error && !license) {
    return <div className="error">{error}</div>;
  }

  if (!license) {
    return <div className="error">{t('admin.license_detail.not_found')}</div>;
  }

  const expired = license.license_type !== 'PERMANENT' && isLicenseExpired(license);

  return (
    <div className="license-detail">
      <h2>{t('admin.license_detail.title')}</h2>
      {error && <div className="error-message">{error}</div>}

      {expired && (
        <div
          className="activate-section"
          style={{ marginBottom: '16px', borderLeft: '4px solid #dc2626' }}
        >
          <p style={{ margin: '0 0 12px 0' }}>
            {t('admin.license_detail.expired_notice')}
          </p>
          <Link to="/admin/licenses" className="btn btn-primary">
            {t('admin.license_detail.go_to_license_management')}
          </Link>
        </div>
      )}

      <div className="detail-card">
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.license_key')}:</strong> {license.license_key}
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.product_code')}:</strong> {license.product_code}
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.type')}:</strong> {license.license_type}
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.user')}:</strong> {license.user_email || '-'}
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.created_at')}:</strong> {formatDate(license.created_at)}
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.activated_at')}:</strong>{' '}
          {license.activated_at ? formatDate(license.activated_at) : '-'}
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.expires_at')}:</strong>{' '}
          {license.expires_at ? formatDate(license.expires_at) : t('admin.license_detail.unlimited')}
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.status')}:</strong>
          <span className={`badge ${license.is_active ? 'badge-success' : 'badge-danger'}`}>
            {license.is_active ? t('admin.common.status.active') : t('admin.common.status.inactive')}
          </span>
        </div>
        <div className="detail-row">
          <strong>{t('admin.license_detail.fields.signature_valid')}:</strong>
          <span className={`badge ${license.signature_valid ? 'badge-success' : 'badge-danger'}`}>
            {license.signature_valid ? t('admin.common.yes') : t('admin.common.no')}
          </span>
        </div>
      </div>

      {!license.user_email && (
        <div className="activate-section">
          <h3>{t('admin.license_detail.activate.title')}</h3>
          <form onSubmit={handleActivate}>
            <div className="form-group">
              <label>{t('admin.license_detail.activate.user_email_label')}</label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                required
                placeholder={t('admin.license_detail.activate.user_email_placeholder')}
              />
            </div>
            <button type="submit" className="btn btn-primary">
              {t('admin.common.buttons.activate')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default LicenseDetail;
